import { toDataURL } from 'qrcode';

import { DisplayPaynow, resolveImageSrc } from '../../api/client';
import { renderHalftoneQr, renderPhotoQr } from './halftone';

/**
 * Load the branding image under its own cache key.
 *
 * crossOrigin keeps the canvas readable when the API is a different origin in
 * development, and the distinct query stops the service worker's opaque,
 * no-cors cache entry (written when the image is shown as a plain preview) from
 * shadowing it.
 */
function loadImage(style: string, url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const base = resolveImageSrc(url);
    const source = base + (base.includes('?') ? '&' : '?') + style + '=1';
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Could not load the branding image.'));
    image.src = source;
  });
}

/**
 * Render the code for one display payload and return a PNG data URL.
 *
 * Shared by the customer display and the settings preview, so what the artist
 * sees while editing is exactly what the customer gets.
 */
export async function renderPaynowQr(paynow: DisplayPaynow): Promise<string> {
  if ((paynow.style === 'halftone' || paynow.style === 'photo') && paynow.logo_url) {
    const image = await loadImage(paynow.style, paynow.logo_url);
    return paynow.style === 'photo'
      ? renderPhotoQr(paynow.payload, image, paynow.color ?? '#000000')
      : renderHalftoneQr(paynow.payload, image, paynow.color ?? '#000000');
  }

  return toDataURL(paynow.payload, {
    // High error correction leaves room for a centre logo to be covered up.
    errorCorrectionLevel: 'H',
    margin: 2,
    width: 480,
    color: { dark: paynow.color ?? '#000000', light: '#ffffff' },
  });
}
